import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useInstitution from 'hooks/useInstitution'
import { hasCapability, hasRoles, ACADEMIC_ROLES } from 'utils/accessPolicy'
import { Icon } from 'components/Icons'
import { globalSearch } from 'service/AdminService'
import styles from './GlobalSearch.module.scss'

const DEBOUNCE_MS = 300

const GROUPS = [
  { key: 'students',    icon: 'student',     labelKey: 'auto.gsearch.group_students',    fallback: 'Студенты' },
  { key: 'employees',   icon: 'briefcase',   labelKey: 'auto.gsearch.group_employees',   fallback: 'Сотрудники' },
  { key: 'groups',      icon: 'group',       labelKey: 'auto.gsearch.group_groups',      fallback: 'Группы' },
  { key: 'orders', icon: 'file-text', fallback: 'Приказы' },
  { key: 'subjects', icon: 'book-open', fallback: 'Дисциплины' },
  { key: 'statements', icon: 'file-text', fallback: 'Ведомости' },
  { key: 'archive_cases', icon: 'archive', fallback: 'Архивные дела' },
]

const ROUTE_FOR_GROUP = {
  students: '/students-list/',
  employees: '/employees/',
  groups: '/groups/',
  documents: '/orders/',
  disciplines: '/my-disciplines/',
}

const GlobalSearch = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const auth = useSelector((state) => state.auth || {})
  const { institution } = useInstitution()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const wrapRef = useRef(null)
  const inputRef = useRef(null)
  const triggerRef = useRef(null)

  // Outside click closes dropdown.
  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const timer = setTimeout(() => inputRef.current?.focus(), 30)
    return () => clearTimeout(timer)
  }, [open])

  // Debounced API call.
  useEffect(() => {
    if (!open) return undefined
    const q = query.trim()
    if (!q) {
      setResults({})
      setError(null)
      setLoading(false)
      return undefined
    }
    setLoading(true)
    setError(null)
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const res = await globalSearch(q)
        const data = res?.data || res || {}
        if (!cancelled) setResults(data)
      } catch (err) {
        if (!cancelled) { setError(err?.message || 'error'); setResults({}) }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [query, open, institution])

  const groupsToRender = useMemo(() => {
    const capabilities = { students: 'students.manage', employees: 'employees.manage', archive_cases: 'archive.manage' }
    return GROUPS.filter((group) => !capabilities[group.key] || hasCapability(auth, capabilities[group.key])).filter((group) => group.key !== 'groups' || hasRoles(auth.roles, [...ACADEMIC_ROLES, 'hr_students', 'hr_employees'], auth.me?.is_superuser)).map((g) => {
      const items = Array.isArray(results?.[g.key]) ? results[g.key] : []
      return { ...g, items }
    }).filter((g) => g.items.length > 0)
  }, [results, auth])

  const handlePick = (group, item) => {
    setOpen(false)
    setQuery('')
    const url = item?.url || item?.link || (item?.id != null ? `${ROUTE_FOR_GROUP[group] || '/'}${item.id}/` : ROUTE_FOR_GROUP[group])
    if (url) navigate(url)
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-label={t('auto.gsearch.open', 'Открыть поиск')}
        aria-expanded={open}
      >
        <Icon name="search" size={16} color="currentColor" />
        <span className={styles.triggerText}>
          {t('auto.gsearch.placeholder_short', 'Поиск...')}
        </span>
      </button>

      {open && (
        <div className={styles.dropdown} role="dialog" aria-label={t('auto.gsearch.dialog', 'Глобальный поиск')}>
          <div className={styles.searchRow}>
            <Icon name="search" size={16} color="var(--color-text-muted, #888)" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('auto.gsearch.placeholder', 'Студенты, сотрудники, группы...')}
              className={styles.input}
              aria-label={t('auto.gsearch.aria_input', 'Поиск')}
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => setQuery('')}
                aria-label={t('auto.gsearch.clear', 'Очистить')}
              >
                <Icon name="x" size={14} color="currentColor" />
              </button>
            )}
          </div>

          <div className={styles.body}>
            {loading && (
              <div className={styles.status}>
                <Icon name="loader" size={14} color="var(--color-accent)" />
                <span>{t('auto.gsearch.searching', 'Идёт поиск...')}</span>
              </div>
            )}
            {!loading && error && (
              <div className={styles.status}>
                <Icon name="alert-circle" size={14} color="var(--color-danger, #c62828)" />
                <span>{t('auto.gsearch.error', 'Ошибка поиска')}</span>
              </div>
            )}
            {!loading && !error && !query.trim() && (
              <div className={styles.hint}>
                {t('auto.gsearch.hint', 'Начните вводить, чтобы увидеть результаты')}
              </div>
            )}
            {!loading && !error && query.trim() && groupsToRender.length === 0 && (
              <div className={styles.hint}>
                {t('auto.gsearch.no_results', 'Ничего не найдено')}
              </div>
            )}
            {groupsToRender.map((g) => (
              <div className={styles.group} key={g.key}>
                <div className={styles.groupTitle}>
                  <Icon name={g.icon} size={12} color="var(--color-text-muted, #888)" />
                  <span>{g.labelKey ? t(g.labelKey, g.fallback) : g.fallback}</span>
                </div>
                {g.items.slice(0, 6).map((it, i) => (
                  <button
                    type="button"
                    key={it.id ?? `${g.key}-${i}`}
                    className={styles.item}
                    onClick={() => handlePick(g.key, it)}
                  >
                    <span className={styles.itemTitle}>
                      {it.title || it.name || it.full_name || it.label || String(it)}
                    </span>
                    {it.subtitle && <span className={styles.itemSubtitle}>{it.subtitle}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
